import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { choosingMatches, choosingWords, type ChoiceRow, type ChoiceStep, type Choosing } from '../tree/choosing.ts';
import { Button } from '../ui.tsx';
import { IMPLIED_HEAD, impliedWords, type NeedsOpensRow, type NeedsOpensRows, type Side } from './needsOpens.ts';

/**
 * What a step needs and what it opens, one step at a time (design study 31, slice 5; §5, A, after Primavera's Trace
 * Logic), on the master's card and a job's setup panel alike: "It needs · 4" then "It opens · 5", each line a row with
 * × Take out; a line a longer way already gives saying so under it, "Already after it, through … Not drawn on the
 * board."; "+ It needs…" and "+ It opens…" to add one; and Next step in the tree, walking the tree in its order. A line
 * on a job waiting on a gate or an inspection not passed is taken out only with a reason, asked under its row; a line
 * the page keeps says why in place of Take out. The rows are the page's (needsOpens.ts); so is every change, and the
 * choosing "+ It needs…" and "+ It opens…" start, on the board (TreeView's `choosing`, its bar ChoosingBar below).
 */
export function NeedsOpens({
  step,
  rows,
  only,
  onTakeOut,
  onAdd,
  next,
  onOpen,
  busy = false,
}: {
  /** The step opened, by name: what each line's Take out and reason name. */
  step: string;
  rows: NeedsOpensRows;
  /** One list alone: the start needs nothing, and nothing comes after the end. */
  only?: Side;
  /** A line taken out (with the reason asked for one that holds): done when it resolves true. Without it, the lists read only. */
  onTakeOut?: (side: Side, key: string, reason?: string) => void | Promise<boolean>;
  /** "+ It needs…" or "+ It opens…" pressed: the page chooses the step on the board. Without it, nothing is added here. */
  onAdd?: (side: Side) => void;
  /** The next step in the tree's walk, opened by the button; none after the last. */
  next: { name: string; onGo: () => void } | null;
  /** A line's step opened by its name. */
  onOpen?: (key: string) => void;
  busy?: boolean;
}) {
  const sides: Side[] = only ? [only] : ['needs', 'opens'];
  return (
    <section aria-label={`What ${step} needs and what it opens`}>
      {sides.map((side) => (
        <div key={side} className="mt-4 first:mt-0">
          <h3 className="label mb-1.5">{`${side === 'needs' ? 'It needs' : 'It opens'} · ${rows[side].length}`}</h3>
          {rows[side].length ? (
            <ul className="border-t border-rule">
              {rows[side].map((r) => (
                <LineRow key={r.key} side={side} step={step} row={r} onTakeOut={onTakeOut} onOpen={onOpen} busy={busy} />
              ))}
            </ul>
          ) : (
            <p className="meta">{side === 'needs' ? 'It waits on nothing yet.' : 'Nothing comes after it yet.'}</p>
          )}
        </div>
      ))}
      {onAdd ? (
        <div className="mt-2 flex flex-wrap items-center gap-x-6">
          {sides.map((side) => (
            <Button key={side} tone="quiet" onClick={() => onAdd(side)}>
              {side === 'needs' ? '+ It needs…' : '+ It opens…'}
            </Button>
          ))}
        </div>
      ) : null}
      {next ? (
        <div className="mt-3">
          <Button tone="secondary" aria-label={`Next step in the tree: ${next.name}`} title={next.name} onClick={next.onGo}>
            Next step in the tree →
          </Button>
        </div>
      ) : null}
    </section>
  );
}

/** One line: the step at its other end, its option, what a longer way already gives, and Take out (a reason asked first where it holds). */
function LineRow({
  side,
  step,
  row: r,
  onTakeOut,
  onOpen,
  busy,
}: {
  side: Side;
  step: string;
  row: NeedsOpensRow;
  onTakeOut?: (side: Side, key: string, reason?: string) => void | Promise<boolean>;
  onOpen?: (key: string) => void;
  busy: boolean;
}) {
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState('');
  const line = side === 'needs' ? `${step} needs ${r.name}` : `${r.name} needs ${step}`;
  // What holds the work back, and what it holds: the step waited on is the one not passed.
  const holder = side === 'needs' ? r.name : step;
  const held = side === 'needs' ? 'this step' : r.name;
  const take = (why?: string) => {
    const done = onTakeOut?.(side, r.key, why);
    if (done instanceof Promise) void done.then((ok) => ok && setAsking(false));
  };
  const name = (
    <>
      {r.name}
      {r.option ? <span className="text-ink-2">{side === 'needs' ? `: ${r.option}` : ` · from “${r.option}”`}</span> : null}
    </>
  );
  const words = r.through ? impliedWords(side, r.through, r.hidden) : null;
  return (
    <li className="border-b border-rule py-1 text-[0.9375rem]">
      <div className="flex min-h-9 items-center gap-3">
        {onOpen ? (
          <button type="button" className="link min-w-0 flex-1 break-words text-left" onClick={() => onOpen(r.key)}>
            {name}
          </button>
        ) : (
          <span className="min-w-0 flex-1 break-words">{name}</span>
        )}
        {r.fixed ? (
          <span className="meta shrink-0">{r.fixed}</span>
        ) : onTakeOut ? (
          <button
            type="button"
            className="hit link shrink-0 text-[0.8125rem] text-ink-2 hover:text-alert disabled:opacity-40"
            aria-label={`Take out: ${line}`}
            disabled={busy}
            onClick={() => (r.holds ? setAsking(true) : take())}
          >
            × Take out
          </button>
        ) : null}
      </div>
      {words ? (
        // The study's words, its head in bold: the line counts, and a longer way already gives it.
        <p className="meta pb-1">
          <b className="font-medium text-ink">{IMPLIED_HEAD[side]}</b>
          {words.slice(IMPLIED_HEAD[side].length)}
        </p>
      ) : null}
      {asking ? (
        <div className="mt-1 mb-2">
          <p className="meta">
            {holder} is not passed: it holds {held} back. The wait is taken out only with the reason, which is on the record.
          </p>
          <label className="mt-1 block">
            <span className="text-sm">{side === 'needs' ? `Why it no longer waits on ${r.name}` : `Why ${r.name} no longer waits on ${step}`}</span>
            <input className="field mt-0.5" value={reason} onChange={(e) => setReason(e.target.value)} />
          </label>
          <div className="mt-2 flex flex-wrap gap-x-4">
            <Button tone="danger" busy={busy} disabled={!reason.trim()} onClick={() => take(reason.trim())}>
              Take the wait out
            </Button>
            <Button tone="quiet" onClick={() => setAsking(false)}>
              Keep it
            </Button>
          </div>
        </div>
      ) : null}
    </li>
  );
}

/**
 * The bar over the board while choosing (design study 31, slice 5; §5, A): "Choosing what Engineering needs: press a step
 * on the board, or type its name" as its field's label, the field, and Cancel; on a phone "…: type its name", the bar and
 * the field alone. The name typed lists the steps it matches in the walk's order (`choosingMatches`), one that cannot be
 * chosen disabled with why; Enter picks the first that can, Esc cancels. `said` is why a step pressed on the board was
 * not picked, in a status line always there (empty until then) so a screen reader hears it. The bar holds to the
 * window's top while the board scrolls under it, as a line asked about does, its matches scrolling inside it: on a tall
 * board the why of a press refused (on a touch screen, the only place it shows), the matches and Cancel stay in view.
 */
export function ChoosingBar({ name, wide, choosing: c, said }: { name: string; wide: boolean; choosing: Choosing & { among: readonly ChoiceStep[] }; said: string | null }) {
  const id = useId();
  const field = useRef<HTMLInputElement>(null);
  const [typed, setTyped] = useState('');
  const { rows, more } = choosingMatches(c.among, typed, c);
  // The field ready to type into, without moving the page off the step being joined.
  useEffect(() => field.current?.focus({ preventScroll: true }), []);
  const keys = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      c.onCancel();
    } else if (e.key === 'Enter') {
      const first = rows.find((r) => !r.why);
      if (!first) return;
      e.preventDefault();
      c.onPick(first.key);
    }
  };
  return (
    <div role="group" aria-label={`Choosing what ${name} ${c.side}`} className="sticky top-2 z-30 mb-3 rounded-[2px] border border-ink bg-paper px-4 py-2.5">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <label htmlFor={id} className="text-[0.9375rem] text-ink">
          {choosingWords(name, c.side, wide)}
        </label>
        <input id={id} ref={field} className="field min-h-11 w-full max-w-72" autoComplete="off" value={typed} onChange={(e) => setTyped(e.target.value)} onKeyDown={keys} />
        <Button tone="quiet" onClick={c.onCancel}>
          Cancel
        </Button>
      </div>
      {/* Always there, empty until a press is refused, so a screen reader is listening when its why comes. */}
      <p role="status" className="mt-1 text-[0.875rem] text-ink-2 empty:mt-0">
        {said}
      </p>
      {typed.trim() ? <ChoiceRows rows={rows} more={more} onPick={c.onPick} /> : null}
    </div>
  );
}

/** The steps a name typed matches while choosing: each a button, one that cannot be chosen disabled, with why under its name. */
export function ChoiceRows({ rows, more, onPick }: { rows: readonly ChoiceRow[]; more: number; onPick: (key: string) => void }) {
  if (!rows.length) return <p className="meta mt-2">No step is called that.</p>;
  return (
    <div className="mt-2 max-w-[36rem]">
      <ul className="max-h-[40vh] overflow-y-auto overscroll-contain divide-y divide-rule border-y border-rule">
        {rows.map((r) => (
          <li key={r.key}>
            <button
              type="button"
              disabled={Boolean(r.why)}
              aria-label={r.why ? `${r.name}: ${r.why}` : `Choose ${r.name}`}
              onClick={() => onPick(r.key)}
              className="block min-h-11 w-full px-2 py-1.5 text-left enabled:hover:bg-inset/60 disabled:cursor-not-allowed"
            >
              <span className={`block text-[0.9375rem] leading-snug ${r.why ? 'text-ink-3' : 'text-ink'}`}>{r.name}</span>
              {r.where ? <span className="block text-[0.8125rem] text-ink-3">{r.where}</span> : null}
              {r.why ? <span className="block text-[0.8125rem] text-ink-2">{r.why}</span> : null}
            </button>
          </li>
        ))}
      </ul>
      {more ? <p className="meta mt-1">{more} more: type more of its name.</p> : null}
    </div>
  );
}
